import React, { useEffect, useRef, useState } from 'react';

import { EnvironmentOutlined, SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import styled from 'styled-components';

import { GOOGLE_PLACES_ADDRESS_COMPONENTS, GOOGLE_PLACES_FIELDS_LIST } from 'constants/google';

type Props = {
  getLocationBySearch: (data: any) => void;
  placeholder?: string;
  style?: any;
  defaultValue?: string;
  disabled?: boolean;
  updateCommunityName?: (data: string) => void;
};

const Wrapper = styled.div`
  position: relative;
`;

const StyledInput = styled(Input)`
  border-radius: 50px;
  font-size: 14px;
  overflow: hidden;
  padding: 0px 15px;
  box-shadow: 1px 2px 5px #ccc;
  height: 50px;
`;

const SuggestionsCard = styled.div`
  padding: 0;
  position: absolute;
  top: 100%;
  border-radius: 0;
  border: 1px solid var(--line-gray);
  box-shadow: 1px 2px 5px var(--box-shadow-gray-dark);
  left: 0;
  right: 0;
  z-index: 999;
`;

const SuggestionItem = styled.div<{ active?: boolean }>`
  cursor: pointer;
  padding: 5px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: ${({ active }) => (active ? 'var(--background-gray-light)' : '#fff')};

  &:hover {
    background-color: var(--background-gray-light);
  }
`;

const LocationSearchInput = ({
  getLocationBySearch,
  placeholder = '',
  style,
  defaultValue,
  disabled = false,
  updateCommunityName,
}: Props) => {
  const [address, setAddress] = useState(defaultValue || '');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(() => {
    if (defaultValue) {
      setAddress(defaultValue);
    }
  }, [defaultValue]);

  const handleChange = async (value: string) => {
    setAddress(value);
    updateCommunityName?.(value);

    if (!value) {
      setSuggestions([]);
      return;
    }

    // @ts-ignore
    const { AutocompleteSuggestion, AutocompleteSessionToken } = google.maps.places;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }

    const { suggestions: predictions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: value,
      sessionToken: sessionTokenRef.current,
    });

    setSuggestions(predictions || []);
  };

  const handleSelect = async (suggestion: any) => {
    const placePrediction = suggestion.placePrediction.toPlace();
    const { place } = await placePrediction.fetchFields({ fields: GOOGLE_PLACES_FIELDS_LIST });

    const values: any = {
      name: place.displayName || '',
      streetNumber: '',
      route: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      formatted_address: place.formattedAddress || '',
      place_id: place.id || '',
      phone_number: place.nationalPhoneNumber || '',
      website: place.websiteURI || '',
      lat: place?.location?.lat(),
      lng: place?.location?.lng(),
    };

    if (place.addressComponents) {
      for (const component of place.addressComponents) {
        const type = component.types[0];
        switch (type) {
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.STREET_NUMBER:
            values.streetNumber = component.longText;
            break;
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.ROUTE:
            values.route = component.shortText;
            break;
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.LOCALITY:
            values.city = component.longText;
            break;
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.POSTAL_TOWN:
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.ADMINISTRATIVE_AREA_LEVEL_3:
            if (!values.city) values.city = component.longText;
            break;
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.ADMINISTRATIVE_AREA_LEVEL_1:
            values.state = component.shortText;
            break;
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.POSTAL_CODE:
            values.zip_code = component.longText;
            break;
          case GOOGLE_PLACES_ADDRESS_COMPONENTS.COUNTRY:
            values.country = component.longText;
            break;
        }
      }
    }

    getLocationBySearch(values);
    setAddress(place.displayName ?? place.formattedAddress);
    setSuggestions([]);
  };

  return (
    <Wrapper>
      <StyledInput
        size="middle"
        prefix={<SearchOutlined />}
        style={style}
        value={address}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />

      {suggestions.length > 0 && (
        <SuggestionsCard className="box_shadow">
          {suggestions.map((sug) => (
            <SuggestionItem key={sug.placeId} onClick={() => handleSelect(sug)}>
              <EnvironmentOutlined />
              {sug.placePrediction.text.toString()}
            </SuggestionItem>
          ))}
        </SuggestionsCard>
      )}
    </Wrapper>
  );
};

export default LocationSearchInput;
